/**
 * Altar del Perfume — keep the header in drawer mode at every width.
 *
 * Horizon decides between an inline menu and a hamburger drawer with a `data-menu-style`
 * attribute on #header-component, recomputed on load, on resize and on touch detection. Pinning
 * it to 'drawer' uses the theme's own supported mode rather than hiding the inline menu with
 * CSS, so the drawer's grid, focus trap and submenu behaviour all come along correctly.
 *
 * The attribute is re-asserted rather than set once, because the theme's own code overwrites it.
 */
const FORCED = 'drawer';

function apply() {
  const header = document.getElementById('header-component');
  // Guarded so the observer below does not retrigger itself.
  if (header && header.dataset.menuStyle !== FORCED) header.dataset.menuStyle = FORCED;
}

let observed = null;
function watch() {
  const header = document.getElementById('header-component');
  if (!header || header === observed) return apply();

  observed = header;
  new MutationObserver(apply).observe(header, {
    attributes: true,
    attributeFilter: ['data-menu-style'],
  });
  apply();
}

watch();
document.addEventListener('DOMContentLoaded', watch);
window.addEventListener('resize', apply);
// The theme editor replaces the whole header section on edit.
document.addEventListener('shopify:section:load', watch);

/**
 * Header que contrasta con lo que tiene debajo.
 *
 * Sobre una sección clara el header se vuelve oscuro (verde pino, iconos marfil) y sobre una
 * oscura se vuelve claro (marfil, iconos botánicos): así nunca se funde con el fondo. Se mira
 * lo que hay justo debajo del borde inferior del header —su posición cambia al hacer scroll
 * porque es sticky— y se busca el primer fondo opaco subiendo por los ancestros. Una sección
 * marcada `.altar-on-dark` (héroes con imagen, bandas oscuras) cuenta como oscura aunque su
 * fondo sea una foto. Con el menú abierto se queda claro, porque el panel es marfil.
 */
function luminance(rgb) {
  const [r, g, b] = rgb.map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function toneAt(el) {
  for (let e = el; e && e !== document.documentElement; e = e.parentElement) {
    if (e.classList?.contains('altar-on-dark')) return 'dark';
    if (e.tagName === 'IMG' || e.tagName === 'VIDEO') return 'dark';
    const m = getComputedStyle(e).backgroundColor.match(/[\d.]+/g);
    if (m && (m.length < 4 || Number(m[3]) > 0.5)) {
      return luminance(m.slice(0, 3).map(Number)) < 0.35 ? 'dark' : 'light';
    }
  }
  return 'light';
}

let ticking = false;
function updateTone() {
  ticking = false;
  const header = document.getElementById('header-component');
  if (!header) return;
  const group = header.closest('.shopify-section-group-header-group') || header;
  let tone = 'light';
  if (!header.querySelector('details[open]')) {
    // Tres puntos a lo ancho y gana la mayoría: el centro solo puede caer sobre una tarjeta
    // clara dentro de una sección oscura, y lo que se ve alrededor del header es la sección.
    const y = Math.min(window.innerHeight - 1, header.getBoundingClientRect().bottom + 2);
    const w = window.innerWidth;
    const votes = [12, w / 2, w - 12].map((x) => {
      const below = document
        .elementsFromPoint(x, y)
        .find((e) => !group.contains(e) && !e.closest('.shopify-section-group-header-group'));
      return below ? toneAt(below) : 'light';
    });
    tone = votes.filter((t) => t === 'dark').length >= 2 ? 'dark' : 'light';
  }
  // Fondo oscuro debajo → header claro; fondo claro debajo → header oscuro.
  const want = tone === 'dark' ? 'light' : 'dark';
  if (header.dataset.altarTone !== want) header.dataset.altarTone = want;
}

function requestTone() {
  if (!ticking) {
    ticking = true;
    requestAnimationFrame(updateTone);
  }
}

// La página no siempre desplaza la ventana (el tema puede desplazar un contenedor), así que se
// escucha el scroll de cualquier elemento en fase de captura.
document.addEventListener('scroll', requestTone, { passive: true, capture: true });
window.addEventListener('resize', requestTone);
window.addEventListener('load', requestTone);
document.addEventListener('DOMContentLoaded', requestTone);
document.addEventListener('shopify:section:load', requestTone);
document.addEventListener('toggle', requestTone, true);
requestTone();
